import type { ApiErrorBody } from "@/shared/types";

/** Base path for the versioned JSON API (proxied to the API server in dev). */
export const API_BASE = "/api/v1";

/**
 * A failed API response. Carries the HTTP status and the server-provided error
 * code/message so callers (and toasts) can react precisely.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** True for 401s, i.e. the caller is not (or no longer) authenticated. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  /** JSON-serializable request body. Sets the content-type automatically. */
  json?: unknown;
}

/**
 * Thin fetch wrapper for the JSON API. Sends/receives JSON, always includes
 * credentials (session cookie), and throws {@link ApiError} on non-2xx so call
 * sites can use try/catch instead of checking `res.ok` everywhere.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options;

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    ...rest,
  });

  if (res.status === 204) {
    return undefined as T;
  }

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const body = (payload ?? {}) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`, body.code);
  }

  return payload as T;
}
